import { useEffect, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import Badge from '../../components/ui/Badge.jsx'
import Button, { IconButton } from '../../components/ui/Button.jsx'
import { Modal } from '../../components/ui/Modal.jsx'
import { Field, Input, Textarea } from '../../components/ui/Field.jsx'
import { useFeedback } from '../../components/ui/feedback.jsx'
import api from '../../lib/api.js'
import { AdminTable, Td } from './AdminTable.jsx'

/*
 * Admin — scheduled tasks management (super-admin). GET /admin/jobs ;
 * PATCH /admin/jobs/:id {enabled} ; POST /admin/jobs/:id/execute (run now).
 * The execution history lives in its own "Job history" tab.
 */
const STATUS_TONE = { success: 'success', completed: 'success', error: 'danger', failed: 'danger', running: 'info', pending: 'neutral', cancelled: 'neutral' }

export default function JobsTab() {
  const { t } = useTranslation()
  const { confirm, toast } = useFeedback()
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [busyId, setBusyId] = useState(null)
  const [editJob, setEditJob] = useState(null)

  const load = useCallback(() => {
    setLoading(true)
    // Sort by id so the order stays stable across reloads — otherwise toggling a
    // job (which bumps updated_at) would make the backend reorder the list.
    api.get('/admin/jobs').then(({ data }) => setRows([...(data?.jobs || [])].sort((a, b) => a.id - b.id))).finally(() => setLoading(false))
  }, [])
  useEffect(() => { load() }, [load])

  const toggle = async (j) => {
    setBusyId(j.id)
    try { await api.patch(`/admin/jobs/${j.id}`, { enabled: !j.enabled }); toast(t(j.enabled ? 'admin.jobs.disabled' : 'admin.jobs.enabled_toast'), 'success'); load() }
    catch { toast(t('admin.errorGeneric'), 'error') } finally { setBusyId(null) }
  }

  const run = async (j) => {
    const ok = await confirm({ tone: 'info', destructive: false, title: t('admin.jobs.confirmRun'), message: j.name, confirmLabel: t('admin.jobs.run'), cancelLabel: t('common.cancel') })
    if (!ok) return
    setBusyId(j.id)
    try { await api.post(`/admin/jobs/${j.id}/execute`); toast(t('admin.jobs.launched'), 'success'); setTimeout(load, 800) }
    catch { toast(t('admin.errorGeneric'), 'error') } finally { setBusyId(null) }
  }

  const columns = [
    { key: 'name', label: t('admin.jobs.name') }, { key: 'schedule', label: t('admin.jobs.schedule') },
    { key: 'last', label: t('admin.jobs.lastRun') }, { key: 'execs', label: t('admin.jobs.executions') },
    { key: 'enabled', label: t('admin.jobs.enabled') }, { key: 'actions', label: t('common.actions'), align: 'right' },
  ]

  return (
    <>
      <AdminTable columns={columns} rows={rows} loading={loading} empty={t('admin.jobs.empty')}
        renderRow={(j) => {
          const total = j.stats?.total_executions ?? 0
          const failed = j.stats?.failed_executions ?? 0
          return (
            <>
              <Td strong>{j.name}{j.description && <div style={{ fontSize: 12, color: 'var(--color-text-muted)', fontWeight: 400, maxWidth: 320 }}>{j.description}</div>}</Td>
              <Td>
                <code style={{ fontSize: 12.5, color: 'var(--color-text)' }}>{j.cron_expression}</code>
                {j.next_run && <div style={{ fontSize: 11.5, color: 'var(--color-text-muted)' }}>{t('admin.jobs.nextRun')} : {fmtTs(j.next_run)}</div>}
              </Td>
              <Td>
                {j.last_run ? <span style={{ fontSize: 13 }}>{fmtTs(j.last_run)}</span> : '—'}
                {j.last_status && <div style={{ marginTop: 3 }}><Badge tone={STATUS_TONE[j.last_status] || 'neutral'}>{j.last_status}</Badge></div>}
                {j.last_status === 'error' && j.last_error && <div title={j.last_error} style={{ fontSize: 11.5, color: 'var(--color-danger)', maxWidth: 220, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{j.last_error}</div>}
              </Td>
              <Td>
                <span style={{ fontSize: 13, color: 'var(--color-ink)', fontWeight: 600 }}>{total}</span>
                {failed > 0 && <span style={{ marginLeft: 8 }}><Badge tone="danger">{t('admin.jobs.failedCount', { count: failed })}</Badge></span>}
              </Td>
              <Td><Badge tone={j.enabled ? 'success' : 'neutral'}>{j.enabled ? t('admin.active') : t('admin.inactive')}</Badge></Td>
              <Td align="right"><div style={{ display: 'flex', gap: 7, justifyContent: 'flex-end' }}>
                <IconButton icon="pencil" variant="primary" title={t('common.edit')} disabled={busyId === j.id} onClick={() => setEditJob(j)} />
                <IconButton icon="send" title={t('admin.jobs.run')} variant="primary" disabled={busyId === j.id} onClick={() => run(j)} />
                <IconButton icon={j.enabled ? 'power' : 'check'} title={t(j.enabled ? 'admin.jobs.disable' : 'admin.jobs.enable')} variant={j.enabled ? 'danger' : 'success'} disabled={busyId === j.id} onClick={() => toggle(j)} />
              </div></Td>
            </>
          )
        }} />
      {editJob && <EditModal t={t} job={editJob} onClose={() => setEditJob(null)} onSaved={() => { setEditJob(null); load() }} />}
    </>
  )
}

function EditModal({ t, job, onClose, onSaved }) {
  const { toast, notify } = useFeedback()
  const [cron, setCron] = useState(job.cron_expression || '')
  const [description, setDescription] = useState(job.description || '')
  const [busy, setBusy] = useState(false)
  const cronInvalid = cron.trim() && ![5, 6].includes(cron.trim().split(/\s+/).length)

  const save = async () => {
    if (cronInvalid) return
    setBusy(true)
    try { await api.patch(`/admin/jobs/${job.id}`, { cron_expression: cron.trim(), description }); toast(t('admin.jobs.saved'), 'success'); onSaved() }
    catch (e) { notify({ tone: 'error', title: t('settings.error'), message: e?.response?.data?.error || t('settings.errorGeneric') }) } finally { setBusy(false) }
  }

  return (
    <Modal open onClose={onClose} maxWidth={520} closeOnBackdrop={false}>
      <div style={{ padding: 24 }}>
        <h3 style={{ fontFamily: 'var(--font-title)', fontWeight: 700, fontSize: 18, marginBottom: 4 }}>{job.name}</h3>
        <p style={{ fontSize: 12.5, color: 'var(--color-text-muted)', fontFamily: 'monospace', marginBottom: 18 }}>{job.handler}</p>
        <Field label={t('admin.jobs.schedule')} required error={cronInvalid ? t('admin.jobs.cronInvalid') : undefined}>
          <Input value={cron} onChange={(e) => setCron(e.target.value)} placeholder="0 2 * * *" style={{ fontFamily: 'monospace', ...(cronInvalid ? { borderColor: 'var(--color-danger)' } : {}) }} />
        </Field>
        <p style={{ fontSize: 12, color: 'var(--color-text-muted)', marginTop: -8, marginBottom: 14 }}>{t('admin.jobs.cronHint')}</p>
        <Field label={t('admin.jobs.descriptionLabel')}><Textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)} /></Field>
        <div style={{ background: 'var(--color-bg-lilac)', borderRadius: 10, padding: '9px 12px', fontSize: 12.5, color: 'var(--color-text)', marginBottom: 18 }}>{t('admin.jobs.scheduleNote')}</div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <Button variant="secondary" onClick={onClose}>{t('common.cancel')}</Button>
          <Button variant="primary" icon="check" loading={busy} disabled={cronInvalid} onClick={save}>{t('common.save')}</Button>
        </div>
      </div>
    </Modal>
  )
}

function fmtTs(ts) {
  if (!ts) return '—'
  const d = new Date(ts)
  return `${d.toLocaleDateString('fr-BE')} ${d.toLocaleTimeString('fr-BE', { hour: '2-digit', minute: '2-digit' })}`
}
